// src/modules/agentic/AgentCoreLab.jsx
// Agent Core — perceive→think→act loop · ReAct · tool calling · agent anatomy

import React, { useState, useEffect, useRef } from 'react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';
import { Panel, Metric, LogPanel, timestamp } from '../../components/ui.jsx';

const AC = '#22d3ee';
import { _livePalette as P } from '../../context/paletteSync';

const Btn = ({onClick,children,color=AC,disabled,style}) => (
  <button onClick={onClick} disabled={disabled} style={{padding:'8px 16px',background:color+'22',border:`1px solid ${color}`,color,fontSize:'10px',cursor:disabled?'not-allowed':'pointer',borderRadius:'3px',fontFamily:'JetBrains Mono',fontWeight:700,letterSpacing:'.08em',opacity:disabled?.4:1,...style}}>{children}</button>
);
const Sl = ({label,value,min,max,step=1,onChange,unit='',color=AC}) => (
  <div style={{marginBottom:'10px'}}>
    <div style={{display:'flex',justifyContent:'space-between',marginBottom:'3px'}}>
      <span style={{fontSize:'10px',color:'var(--t2)'}}>{label}</span>
      <span style={{fontSize:'10px',color,fontWeight:700}}>{typeof value==='number'?value.toFixed(step<0.01?4:step<1?2:0):value}{unit}</span>
    </div>
    <div style={{position:'relative',height:'3px',background:'var(--b0)',borderRadius:'2px'}}>
      <div style={{position:'absolute',left:0,height:'100%',background:`linear-gradient(90deg,${color}44,${color})`,width:`${((value-min)/(max-min))*100}%`}}/>
      <input type="range" min={min} max={max} step={step} value={value} onChange={e=>onChange(parseFloat(e.target.value))}
        style={{position:'absolute',inset:'-7px 0',width:'100%',opacity:0,cursor:'pointer',height:'18px'}}/>
    </div>
  </div>
);

// ─── PERCEIVE→THINK→ACT LOOP SVG ─────────────────────────────────────────────
function AgentLoopSVG({ step, running }) {
  const W=600, H=260, cx=300, cy=130, R=90;
  const nodes = [
    { label:'PERCEIVE',  sub:'observe env',  angle:-90,  color:'#22d3ee' },
    { label:'THINK',     sub:'reason & plan', angle:30,   color:'#a78bfa' },
    { label:'ACT',       sub:'tool / output', angle:150,  color:'#4ade80' },
  ];
  const toRad = d => d*Math.PI/180;
  const active = step % 3;

  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{background:'#060b14',borderRadius:'8px',border:'1px solid #1e293b',display:'block'}}>
      {/* Orbit ring */}
      <circle cx={cx} cy={cy} r={R} fill="none" stroke="#1e293b" strokeWidth={1.5} strokeDasharray="6 4"/>

      {/* Arc arrows between nodes */}
      {nodes.map((n,i) => {
        const next = nodes[(i+1)%3];
        const a1=toRad(n.angle), a2=toRad(next.angle);
        const x1=cx+R*Math.cos(a1), y1=cy+R*Math.sin(a1);
        const x2=cx+R*Math.cos(a2), y2=cy+R*Math.sin(a2);
        const mx=cx+(R+28)*Math.cos((a1+a2)/2), my=cy+(R+28)*Math.sin((a1+a2)/2);
        const isActive = active===i && running;
        return <path key={i} d={`M${x1},${y1} Q${mx},${my} ${x2},${y2}`}
          fill="none" stroke={isActive?n.color:'#334155'} strokeWidth={isActive?2.5:1.5}
          strokeDasharray={isActive?'none':'5 3'} style={{transition:'stroke .3s'}}/>;
      })}

      {/* Center brain */}
      <circle cx={cx} cy={cy} r={32} fill="#0f172a" stroke={running?P[active]:'#334155'} strokeWidth={2}
        style={{transition:'stroke .3s'}}/>
      <text x={cx} y={cy-5} textAnchor="middle" style={{fontSize:'11px',fill:'var(--t1)',fontFamily:'Syne',fontWeight:800}}>AGENT</text>
      <text x={cx} y={cy+10} textAnchor="middle" style={{fontSize:'8px',fill:'var(--t3)',fontFamily:'JetBrains Mono'}}>LLM core</text>

      {/* Nodes */}
      {nodes.map((n,i) => {
        const a=toRad(n.angle), nx=cx+R*Math.cos(a), ny=cy+R*Math.sin(a);
        const isActive = active===i && running;
        return (
          <g key={i}>
            <circle cx={nx} cy={ny} r={26} fill={isActive?n.color+'28':'#0f172a'} stroke={n.color} strokeWidth={isActive?2.5:1.5} style={{transition:'all .3s'}}/>
            <text x={nx} y={ny-4} textAnchor="middle" style={{fontSize:'8px',fill:n.color,fontFamily:'JetBrains Mono',fontWeight:700}}>{n.label}</text>
            <text x={nx} y={ny+8} textAnchor="middle" style={{fontSize:'7px',fill:'#64748b',fontFamily:'JetBrains Mono'}}>{n.sub}</text>
          </g>
        );
      })}

      {/* External elements */}
      {/* Environment (top left) */}
      <rect x={20} y={20} width={90} height={34} rx={3} fill="#22d3ee10" stroke="#22d3ee44"/>
      <text x={65} y={33} textAnchor="middle" style={{fontSize:'8px',fill:'#22d3ee',fontFamily:'JetBrains Mono',fontWeight:700}}>ENVIRONMENT</text>
      <text x={65} y={45} textAnchor="middle" style={{fontSize:'7px',fill:'#64748b',fontFamily:'JetBrains Mono'}}>observation</text>
      <line x1={110} y1={37} x2={cx+R*Math.cos(toRad(-90))-14} y2={cy+R*Math.sin(toRad(-90))-14} stroke="#22d3ee33" strokeDasharray="4 2"/>

      {/* Tools (right) */}
      <rect x={490} y={55} width={100} height={34} rx={3} fill="#4ade8010" stroke="#4ade8044"/>
      <text x={540} y={68} textAnchor="middle" style={{fontSize:'8px',fill:'#4ade80',fontFamily:'JetBrains Mono',fontWeight:700}}>TOOLS</text>
      <text x={540} y={80} textAnchor="middle" style={{fontSize:'7px',fill:'#64748b',fontFamily:'JetBrains Mono'}}>actions / APIs</text>
      <line x1={490} y1={72} x2={cx+R*Math.cos(toRad(150))+22} y2={cy+R*Math.sin(toRad(150))+2} stroke="#4ade8033" strokeDasharray="4 2"/>

      {/* Memory (bottom) */}
      <rect x={230} y={215} width={140} height={34} rx={3} fill="#a78bfa10" stroke="#a78bfa44"/>
      <text x={300} y={228} textAnchor="middle" style={{fontSize:'8px',fill:'#a78bfa',fontFamily:'JetBrains Mono',fontWeight:700}}>MEMORY</text>
      <text x={300} y={240} textAnchor="middle" style={{fontSize:'7px',fill:'#64748b',fontFamily:'JetBrains Mono'}}>short + long term</text>
      <line x1={300} y1={215} x2={cx+R*Math.cos(toRad(30))-5} y2={cy+R*Math.sin(toRad(30))+22} stroke="#a78bfa33" strokeDasharray="4 2"/>

      {/* Step label */}
      {running && <text x={cx} y={H-8} textAnchor="middle" style={{fontSize:'9px',fill:P[active],fontFamily:'JetBrains Mono',fontWeight:700}}>STEP: {nodes[active].label}</text>}
    </svg>
  );
}

// ─── REACT TRACE SVG ─────────────────────────────────────────────────────────
function ReActTrace({ steps }) {
  return (
    <div style={{display:'flex',flexDirection:'column',gap:'3px'}}>
      {steps.map((s,i) => (
        <div key={i} style={{display:'flex',gap:'10px',padding:'8px 12px',background:'var(--deep)',borderRadius:'3px',borderLeft:`3px solid ${s.type==='thought'?'#a78bfa':s.type==='action'?'#4ade80':s.type==='observation'?'#22d3ee':'#fbbf24'}`,animation:'fadeIn .3s'}}>
          <span style={{fontSize:'9px',fontFamily:'JetBrains Mono',fontWeight:700,color:s.type==='thought'?'#a78bfa':s.type==='action'?'#4ade80':s.type==='observation'?'#22d3ee':'#fbbf24',minWidth:80,textTransform:'uppercase'}}>{s.type}</span>
          <span style={{fontSize:'10px',color:'var(--t2)',flex:1,lineHeight:1.5}}>{s.content}</span>
        </div>
      ))}
    </div>
  );
}

// ─── REACT SIMULATION ────────────────────────────────────────────────────────
const REACT_DEMOS = {
  math: {
    task: 'What is the population of Tokyo multiplied by 3.14?',
    trace: [
      {type:'thought',    content:'I need to find the current population of Tokyo, then multiply it by 3.14.'},
      {type:'action',     content:'search("Tokyo population 2024")'},
      {type:'observation',content:'Tokyo population: approximately 13.96 million (city), 37.4 million (metro area).'},
      {type:'thought',    content:'I\'ll use the metro area: 37,400,000 × 3.14 = ?'},
      {type:'action',     content:'calculator("37400000 * 3.14")'},
      {type:'observation',content:'Result: 117,436,000'},
      {type:'answer',     content:'The population of Tokyo metro area (37.4M) multiplied by 3.14 ≈ 117,436,000.'},
    ]
  },
  code: {
    task: 'Find and fix the bug in my Python code that crashes on empty lists.',
    trace: [
      {type:'thought',    content:'I should read the code file first, then analyze for empty-list edge cases.'},
      {type:'action',     content:'read_file("script.py")'},
      {type:'observation',content:'def get_max(lst): return max(lst)  — no guard for empty input.'},
      {type:'thought',    content:'max() raises ValueError on empty list. Need to add a guard.'},
      {type:'action',     content:'edit_file("script.py", "def get_max(lst): return max(lst) if lst else None")'},
      {type:'observation',content:'File saved. Running tests...'},
      {type:'action',     content:'run_tests("test_script.py")'},
      {type:'observation',content:'All 4 tests passed. No errors.'},
      {type:'answer',     content:'Bug fixed: added empty-list guard. get_max([]) now returns None instead of crashing.'},
    ]
  },
  research: {
    task: 'Summarize the key differences between GPT-4 and Claude 3.5.',
    trace: [
      {type:'thought',    content:'I should search for recent comparisons and pull key facts from reliable sources.'},
      {type:'action',     content:'search("GPT-4 vs Claude 3.5 comparison 2024")'},
      {type:'observation',content:'Multiple articles found: benchmarks, context window, pricing data.'},
      {type:'action',     content:'fetch_url("anthropic.com/claude-3-5-sonnet")'},
      {type:'observation',content:'Claude 3.5: 200K context, strong coding/reasoning, Constitutional AI safety.'},
      {type:'action',     content:'fetch_url("openai.com/gpt-4")'},
      {type:'observation',content:'GPT-4: 128K context, multimodal, Code Interpreter, broad plugin ecosystem.'},
      {type:'thought',    content:'I have enough data to write a comparison summary.'},
      {type:'answer',     content:'Key differences: Context (200K vs 128K), Safety approach (Constitutional vs RLHF), Ecosystem (fewer plugins vs rich plugin store), Coding (Claude slightly stronger per benchmarks).'},
    ]
  }
};

// ─── ANATOMY ─────────────────────────────────────────────────────────────────
const ANATOMY = [
  {name:'LLM Core',     icon:'🧠', desc:'The reasoning engine. Receives context, produces next action or answer. GPT-4, Claude, Gemini, Llama.', color:'#a78bfa'},
  {name:'Perceiver',    icon:'👁', desc:'Converts environment signals into tokens: user messages, tool results, sensor data, document chunks.', color:'#22d3ee'},
  {name:'Memory',       icon:'💾', desc:'Short-term: the context window. Long-term: vector DB, key-value store, episodic history.', color:'#4ade80'},
  {name:'Tool Layer',   icon:'🔧', desc:'APIs, code executors, web search, databases, file systems. Defined as JSON schemas, called via function calling.', color:'#fbbf24'},
  {name:'Planner',      icon:'📋', desc:'Decomposes goals into sub-tasks. Chain-of-Thought, Tree-of-Thought, MCTS. May be a separate LLM call.', color:'#fb923c'},
  {name:'Executor',     icon:'⚡', desc:'Actually runs actions: HTTP calls, code execution, writing files, spawning sub-agents.', color:'#f87171'},
];

const TABS = ['loop','react','anatomy','agent-types','limitations'];

export default function AgentCoreLab() {
  const [tab, setTab] = useState('loop');
  const [logs, setLogs] = useState([{ts:'00:00:00',msg:'Agent Core Lab — the perceive→think→act loop that powers all autonomous AI agents.',t:'info'}]);
  const log = (msg,t='info') => setLogs(l=>[...l.slice(-80),{ts:timestamp(),msg,t}]);

  const [loopStep, setLoopStep] = useState(0);
  const [loopRunning, setLoopRunning] = useState(false);
  const loopRef = useRef();

  const [reactDemo, setReactDemo] = useState('math');
  const [reactStep, setReactStep] = useState(0);
  const [reactRunning, setReactRunning] = useState(false);
  const reactRef = useRef();

  const [maxSteps, setMaxSteps] = useState(6);
  const [toolSuccess, setToolSuccess] = useState(0.9);

  const toggleLoop = () => {
    if(loopRunning){ clearInterval(loopRef.current); setLoopRunning(false); return; }
    setLoopRunning(true);
    loopRef.current = setInterval(()=>setLoopStep(s=>s+1), 900);
  };

  const runReAct = () => {
    setReactStep(0); setReactRunning(true);
    const trace = REACT_DEMOS[reactDemo].trace;
    let i=0;
    reactRef.current = setInterval(()=>{
      i++;
      setReactStep(i);
      if(i>=trace.length){ clearInterval(reactRef.current); setReactRunning(false); log(`ReAct complete: ${i} steps for "${REACT_DEMOS[reactDemo].task.slice(0,40)}…"`,'ok'); }
    }, 700);
  };

  useEffect(()=>()=>{clearInterval(loopRef.current);clearInterval(reactRef.current);},[]);

  const trace = REACT_DEMOS[reactDemo].trace.slice(0, reactStep);

  // Simulated agent run metrics
  const runMetrics = Array.from({length:20},(_,i)=>({
    trial: i+1,
    success: Math.random()<toolSuccess?1:0,
    steps: Math.floor(2+Math.random()*(maxSteps-2)),
    tokens: Math.floor(800+Math.random()*3000),
  }));
  const successRate = runMetrics.filter(r=>r.success).length/runMetrics.length;
  const avgSteps = (runMetrics.reduce((s,r)=>s+r.steps,0)/runMetrics.length).toFixed(1);

  return (
    <div className="anim-fadeup" style={{display:'flex',flexDirection:'column',gap:'12px'}}>
      <div style={{display:'flex',gap:'3px',background:'var(--deep)',padding:'4px',borderRadius:'3px',border:'1px solid var(--b0)',overflowX:'auto'}}>
        {TABS.map(t=><button key={t} onClick={()=>setTab(t)} style={{padding:'6px 14px',border:`1px solid ${tab===t?AC:'transparent'}`,background:tab===t?AC+'18':'transparent',color:tab===t?AC:'var(--t3)',fontSize:'10px',cursor:'pointer',borderRadius:'2px',fontFamily:'JetBrains Mono',whiteSpace:'nowrap',fontWeight:tab===t?700:400}}>{t.toUpperCase()}</button>)}
      </div>

      {/* ── LOOP ── */}
      {tab==='loop'&&(
        <div style={{display:'grid',gridTemplateColumns:'1fr 260px',gap:'12px'}}>
          <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
            <Panel title="PERCEIVE → THINK → ACT — THE AGENT LOOP" accent={AC}>
              <AgentLoopSVG step={loopStep} running={loopRunning}/>
              <div style={{display:'flex',gap:'8px',marginTop:'10px'}}>
                <Btn onClick={toggleLoop} color={loopRunning?'#f87171':AC} style={{flex:1}}>{loopRunning?'PAUSE LOOP':'▶ RUN LOOP'}</Btn>
                <Btn onClick={()=>{setLoopStep(0);setLoopRunning(false);clearInterval(loopRef.current);}} color="#64748b">RESET</Btn>
              </div>
            </Panel>
            <Panel title="LOOP STEP BREAKDOWN" accent="#4ade80">
              <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:'8px'}}>
                {[
                  {phase:'PERCEIVE',color:'#22d3ee',steps:['Read user message','Get tool results','Read memory / context','Parse env state']},
                  {phase:'THINK',color:'#a78bfa',steps:['Reason about goal','Select next action','Plan sub-tasks','Evaluate options']},
                  {phase:'ACT',color:'#4ade80',steps:['Call tool / API','Write to memory','Generate output','Spawn sub-agent']},
                ].map(({phase,color,steps})=>(
                  <div key={phase} style={{padding:'10px',background:'var(--deep)',borderRadius:'3px',borderTop:`2px solid ${color}`}}>
                    <div style={{fontSize:'10px',color,fontFamily:'JetBrains Mono',fontWeight:700,marginBottom:'7px'}}>{phase}</div>
                    {steps.map(s=><div key={s} style={{fontSize:'9px',color:'var(--t3)',marginBottom:'4px',paddingLeft:'8px',borderLeft:`1px solid ${color}44`}}>{s}</div>)}
                  </div>
                ))}
              </div>
            </Panel>
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
            <Panel title="LOOP STATS (SIM)" accent="#fbbf24">
              <Sl label="Max steps/run" value={maxSteps} min={2} max={20} step={1} onChange={setMaxSteps} color="#fbbf24"/>
              <Sl label="Tool success rate" value={toolSuccess} min={0.3} max={1.0} step={0.05} onChange={setToolSuccess} color="#4ade80"/>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'6px',marginTop:'4px'}}>
                <Metric label="Success rate" value={`${(successRate*100).toFixed(0)}%`} color="#4ade80"/>
                <Metric label="Avg steps" value={avgSteps} color="#fbbf24"/>
                <Metric label="Avg tokens" value={(runMetrics.reduce((s,r)=>s+r.tokens,0)/runMetrics.length).toFixed(0)} color="#22d3ee"/>
                <Metric label="Failures" value={runMetrics.filter(r=>!r.success).length} color="#f87171"/>
              </div>
            </Panel>
            <Panel title="LOOP CYCLES" accent="#22d3ee">
              <ResponsiveContainer width="100%" height={160}>
                <BarChart data={runMetrics.slice(0,12)}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                  <XAxis dataKey="trial" tick={{fontSize:8,fill:'var(--t3)'}}/>
                  <YAxis tick={{fontSize:8,fill:'var(--t3)'}}/>
                  <Tooltip/>
                  <Bar dataKey="steps" radius={[2,2,0,0]}>
                    {runMetrics.slice(0,12).map((r,i)=><Cell key={i} fill={r.success?'#4ade80':'#f87171'} opacity={0.85}/>)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              <div style={{fontSize:'8px',color:'var(--t3)',textAlign:'center',marginTop:'4px'}}>Green=success, Red=failed — steps per trial</div>
            </Panel>
            <LogPanel logs={logs} height={80}/>
          </div>
        </div>
      )}

      {/* ── REACT ── */}
      {tab==='react'&&(
        <div style={{display:'grid',gridTemplateColumns:'240px 1fr',gap:'12px'}}>
          <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
            <Panel title="REACT DEMO" accent="#a78bfa">
              <div style={{fontSize:'9px',color:'var(--t3)',letterSpacing:'.1em',marginBottom:'5px'}}>SELECT TASK</div>
              {Object.entries(REACT_DEMOS).map(([k,v])=>(
                <button key={k} onClick={()=>{setReactDemo(k);setReactStep(0);}} style={{display:'block',width:'100%',padding:'8px',background:reactDemo===k?'#a78bfa18':'var(--deep)',border:`1px solid ${reactDemo===k?'#a78bfa':'var(--b0)'}`,color:reactDemo===k?'#a78bfa':'var(--t3)',fontSize:'9px',cursor:'pointer',borderRadius:'3px',textAlign:'left',marginBottom:'4px',fontFamily:'JetBrains Mono',fontWeight:reactDemo===k?700:400}}>
                  {k.toUpperCase()}
                  <div style={{fontSize:'8px',color:'var(--t3)',fontWeight:400,marginTop:'2px'}}>{v.task.slice(0,42)}…</div>
                </button>
              ))}
              <Btn onClick={runReAct} disabled={reactRunning} color="#a78bfa" style={{width:'100%',marginTop:'6px'}}>{reactRunning?'RUNNING…':'RUN ReAct →'}</Btn>
            </Panel>
            <Panel title="HOW ReAct WORKS" accent="#fbbf24">
              <div style={{fontSize:'9px',color:'var(--t3)',lineHeight:1.8}}>
                <div style={{color:'#fbbf24',fontWeight:700,marginBottom:'4px'}}>ReAct = Reason + Act</div>
                Interleaves Thought, Action, Observation steps. LLM explicitly reasons before each action. Reduces hallucination vs pure chain-of-thought.
                <div style={{marginTop:'8px',fontFamily:'JetBrains Mono',fontSize:'8px',color:'#a78bfa'}}>
                  Thought → Action → Obs → Thought → …
                </div>
              </div>
            </Panel>
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
            <Panel title={`ReAct TRACE — "${REACT_DEMOS[reactDemo].task.slice(0,50)}…"`} accent="#a78bfa">
              <div style={{marginBottom:'10px',display:'flex',gap:'8px',flexWrap:'wrap'}}>
                {[['thought','#a78bfa'],['action','#4ade80'],['observation','#22d3ee'],['answer','#fbbf24']].map(([t,c])=>(
                  <span key={t} style={{padding:'2px 8px',background:c+'18',border:`1px solid ${c}44`,borderRadius:'2px',fontSize:'8px',color:c,fontFamily:'JetBrains Mono'}}>{t}</span>
                ))}
              </div>
              {trace.length>0 ? <ReActTrace steps={trace}/> : <div style={{height:120,display:'flex',alignItems:'center',justifyContent:'center',color:'var(--t3)',fontSize:'11px',border:'1px dashed var(--b1)',borderRadius:'4px'}}>← Select a task and click RUN ReAct</div>}
              {!reactRunning && trace.length>0 && (
                <div style={{marginTop:'10px',display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:'8px'}}>
                  <Metric label="Total steps" value={trace.length} color="#a78bfa"/>
                  <Metric label="Actions" value={trace.filter(s=>s.type==='action').length} color="#4ade80"/>
                  <Metric label="Observations" value={trace.filter(s=>s.type==='observation').length} color="#22d3ee"/>
                  <Metric label="Thoughts" value={trace.filter(s=>s.type==='thought').length} color="#fbbf24"/>
                </div>
              )}
            </Panel>
          </div>
        </div>
      )}

      {/* ── ANATOMY ── */}
      {tab==='anatomy'&&(
        <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
          <Panel title="AGENT ANATOMY — 6 CORE COMPONENTS" accent={AC}>
            <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:'10px'}}>
              {ANATOMY.map(a=>(
                <div key={a.name} style={{padding:'14px',background:'var(--deep)',borderRadius:'4px',borderLeft:`3px solid ${a.color}`}}>
                  <div style={{fontSize:'18px',marginBottom:'6px'}}>{a.icon}</div>
                  <div style={{fontSize:'11px',color:a.color,fontFamily:'JetBrains Mono',fontWeight:700,marginBottom:'6px'}}>{a.name}</div>
                  <div style={{fontSize:'9px',color:'var(--t3)',lineHeight:1.7}}>{a.desc}</div>
                </div>
              ))}
            </div>
          </Panel>
          <Panel title="CONTEXT WINDOW IS EVERYTHING — THE AGENT'S WORKING MEMORY" accent="#fbbf24">
            <div style={{display:'flex',gap:'3px',marginBottom:'8px'}}>
              {[
                {label:'SYSTEM PROMPT',width:'15%',color:'#a78bfa'},
                {label:'TOOLS',width:'10%',color:'#fbbf24'},
                {label:'HISTORY',width:'25%',color:'#22d3ee'},
                {label:'TOOL RESULTS',width:'20%',color:'#4ade80'},
                {label:'CURRENT TASK',width:'12%',color:'#fb923c'},
                {label:'FREE',width:'18%',color:'#334155'},
              ].map(seg=>(
                <div key={seg.label} style={{width:seg.width,padding:'10px 6px',background:seg.color+'22',border:`1px solid ${seg.color}44`,borderRadius:'2px',display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',minHeight:50}}>
                  <span style={{fontSize:'7px',color:seg.color,fontFamily:'JetBrains Mono',fontWeight:700,textAlign:'center',letterSpacing:'.05em'}}>{seg.label}</span>
                </div>
              ))}
            </div>
            <div style={{fontSize:'9px',color:'var(--t3)',lineHeight:1.7}}>Everything the agent knows in one forward pass. When this fills up — the agent loses earlier context. Compressing, summarizing, or externalizing to memory is critical for long tasks.</div>
          </Panel>
        </div>
      )}

      {/* ── AGENT TYPES ── */}
      {tab==='agent-types'&&(
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'10px'}}>
          {[
            {name:'Reflex Agent',color:'#22d3ee',icon:'⚡',desc:'No memory. Direct perception→action mapping. Fast but brittle. Can\'t handle novel situations.',examples:'Spam filter, thermostat, simple chatbot rules',when:'Simple, well-defined, stateless tasks'},
            {name:'Goal-Based Agent',color:'#4ade80',icon:'🎯',desc:'Has a goal. Searches for action sequences to reach it. Uses planning algorithms.',examples:'Chess engine, route planner, puzzle solver',when:'Clear goal, measurable success criteria'},
            {name:'Utility-Based Agent',color:'#a78bfa',icon:'📊',desc:'Maximizes a utility function. Handles trade-offs and uncertainty. Basis for RLHF.',examples:'Ad bidder, trading bot, recommendation system',when:'Multiple competing objectives, optimization needed'},
            {name:'Learning Agent',color:'#fbbf24',icon:'📚',desc:'Improves from experience. Has critic, performance element, learning element, problem generator.',examples:'GPT fine-tuning, AlphaGo, RL agents',when:'Domain too complex to hand-engineer, data available'},
            {name:'LLM Agent',color:'#fb923c',icon:'🧠',desc:'LLM as reasoning core. ReAct, tool use, memory. Emergent generalization from pretraining.',examples:'Claude Artifacts, GitHub Copilot, AutoGPT',when:'Open-ended tasks needing language + reasoning'},
            {name:'Multi-Agent System',color:'#f87171',icon:'🕸',desc:'Multiple agents collaborating. Roles: orchestrator, critic, specialist. Parallelizes complex work.',examples:'AutoGen, CrewAI, MetaGPT',when:'Tasks too large/diverse for one agent'},
          ].map(a=>(
            <Panel key={a.name} title={`${a.icon} ${a.name}`} accent={a.color}>
              <div style={{fontSize:'9px',color:'var(--t3)',lineHeight:1.7,marginBottom:'8px'}}>{a.desc}</div>
              <div style={{fontSize:'9px',marginBottom:'4px'}}><span style={{color:'var(--t3)'}}>Examples: </span><span style={{color:a.color}}>{a.examples}</span></div>
              <div style={{fontSize:'9px'}}><span style={{color:'var(--t3)'}}>Use when: </span><span style={{color:'var(--t2)'}}>{a.when}</span></div>
            </Panel>
          ))}
        </div>
      )}

      {/* ── LIMITATIONS ── */}
      {tab==='limitations'&&(
        <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
          <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:'10px'}}>
            {[
              {name:'Context Limits',color:'#f87171',icon:'📏',prob:'Agent forgets earlier context in long tasks',impact:'High',mitigation:'Compress/summarize, external memory, RAG'},
              {name:'Hallucination',color:'#f87171',icon:'👻',prob:'Agent fabricates tool results or reasoning steps',impact:'Critical',mitigation:'Grounding, verification tools, confidence scores'},
              {name:'Reward Hacking',color:'#fb923c',icon:'🎭',prob:'Agent finds unexpected ways to satisfy objectives',impact:'High',mitigation:'RLHF, Constitutional AI, careful utility design'},
              {name:'Error Propagation',color:'#fbbf24',icon:'🔗',prob:'Early mistakes compound across long chains',impact:'High',mitigation:'Step verification, checkpointing, reflection'},
              {name:'Tool Misuse',color:'#fbbf24',icon:'🔧',prob:'Incorrect tool parameters cause silent failures',impact:'Medium',mitigation:'Typed schemas, validation, retry logic'},
              {name:'Cost Explosion',color:'#4ade80',icon:'💸',prob:'Unbounded loops rack up massive token/API costs',impact:'Medium',mitigation:'Max step budgets, cost monitoring, early stopping'},
            ].map(l=>(
              <div key={l.name} style={{padding:'12px',background:'var(--deep)',borderRadius:'4px',borderTop:`2px solid ${l.color}`}}>
                <div style={{fontSize:'16px',marginBottom:'5px'}}>{l.icon}</div>
                <div style={{fontSize:'10px',color:l.color,fontWeight:700,marginBottom:'5px',fontFamily:'JetBrains Mono'}}>{l.name}</div>
                <div style={{fontSize:'9px',color:'var(--t3)',lineHeight:1.6,marginBottom:'5px'}}>{l.prob}</div>
                <div style={{fontSize:'9px'}}><span style={{color:'var(--t3)'}}>Impact: </span><span style={{color:l.impact==='Critical'?'#f87171':l.impact==='High'?'#fb923c':'#fbbf24',fontWeight:700}}>{l.impact}</span></div>
                <div style={{fontSize:'9px',marginTop:'3px'}}><span style={{color:'var(--t3)'}}>Fix: </span><span style={{color:'#4ade80'}}>{l.mitigation}</span></div>
              </div>
            ))}
          </div>
          <LogPanel logs={logs} height={70}/>
        </div>
      )}
    </div>
  );
}
